import React, { useEffect, useState } from 'react';
import useAolSaveHandler from '../../../../app/useAolSaveHandler';
import { CargoTankTemperatureTypeEnum, NoonReportSectionEnum } from '../../../../types/ViewModel/AolEnums';
import { NoonReportSectionData } from '../../../../types/ViewModel/NoonReportSectionData';
import { hasMissingValues } from '../../../../utils/aolFieldsHelper';
import OdfjellInputComponent, { InputChangeData } from '../../../common/odfjell-input-component/OdfjellInputComponent';
import { CargoTankTemperaturesWrapper, GridWrapper } from './CargoTankTemperatures.style';
import { ListHeaderStyle, SectionInnerWrapper, SubSectionHeader } from '../../aol-container/AolContainer.style';
import { ErrorText } from '../../../shared/error-text/ErrorText.style';
import { CargoTankTemperature } from '../../../../types/ViewModel/CargoTankTemperature';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AolDataItem } from '../../../../types/ViewModel/AolItem';

type Props = {
    vm: any;
    onChange: () => void;
    type: CargoTankTemperatureTypeEnum;
    hasEmptyValues;
    sectionData?: NoonReportSectionData;
    aolDataItem: AolDataItem | null;
    isReadOnly: boolean;
};

const CargoTankTemperaturesComponent: React.FC<Props> = (props) => {
    const [hasEmptyValuesLocal, setHasEmptyValuesLocal] = useState(false);
    const { saveAolEvent } = useAolSaveHandler();

    useEffect(() => {
        if (
            saveAolEvent &&
            saveAolEvent.section === NoonReportSectionEnum.CargoTemperatureControl &&
            props.aolDataItem &&
            saveAolEvent.aolDataItem.id === props.aolDataItem.id
        ) {
            if (saveAolEvent.type === 'save_section') checkOnSave();
            if (saveAolEvent.type === 'save_section_done') initVM();
        }
    }, [saveAolEvent]);

    useEffect(() => {
        if (!props) return;
        initVM();
    }, [props]);

    const initVM = () => {
        setHasEmptyValuesLocal(false);
    };

    const onInputChange = (changeObject: InputChangeData) => {
        const name = changeObject.event.target.name;
        const value = changeObject.event.target.type === 'checkbox' ? changeObject.event.target.checked : changeObject.event.target.value;

        if (changeObject.event.target.type === 'checkbox') {
            changeObject.dataObject[name] = value;
        } else {
            changeObject.dataObject[name] = value ? +value : undefined;
        }

        if (hasEmptyValuesLocal) setHasEmptyValuesLocal(false);
        props.onChange();
    };

    const checkOnSave = () => {
        let ignoreList: string[] = [];
        const hasMissing = getCargoTankTemperatureList()?.some((o) => {
            if (!o.isOnBoard) {
                ignoreList = ['temperature', 'cargoLoadingTemperature', 'tradeName'];
            }
            return hasMissingValues(o, ignoreList);
        });

        if (hasMissing) {
            setHasEmptyValuesLocal(true);
            props.hasEmptyValues.current = true;
        } else {
            if (hasEmptyValuesLocal) setHasEmptyValuesLocal(false);
            props.hasEmptyValues.current = false;
        }
    };

    const getHeaderString = (): string => {
        switch (props.type) {
            case CargoTankTemperatureTypeEnum.InhibitedCargo:
                return 'Inhibited cargoes';
            case CargoTankTemperatureTypeEnum.HeatedAndCooledCargo:
                return 'Heated and cooled cargoes';
            default:
                return 'Cargoes';
        }
    };
    const showSection = (): boolean => {
        return (
            (props.type === CargoTankTemperatureTypeEnum.InhibitedCargo && props.vm.inhibitedCargoTankTemperatures?.length > 0) ||
            (props.type === CargoTankTemperatureTypeEnum.HeatedAndCooledCargo && props.vm.heatedAndCooledCargoTankTemperatures?.length > 0) ||
            (props.type === CargoTankTemperatureTypeEnum.OtherCargo && props.vm.otherCargoTankTemperatures?.length > 0)
        );
    };
    const getCargoTankTemperatureList = (): CargoTankTemperature[] => {
        switch (props.type) {
            case CargoTankTemperatureTypeEnum.InhibitedCargo:
                return props.vm.inhibitedCargoTankTemperatures;
            case CargoTankTemperatureTypeEnum.HeatedAndCooledCargo:
                return props.vm.heatedAndCooledCargoTankTemperatures;
            default:
                return props.vm.otherCargoTankTemperatures;
        }
    };

    return (
        <>
            {showSection() && (
                <SectionInnerWrapper>
                    <SubSectionHeader className="flex-space-between">
                        <span>{getHeaderString()}</span>
                    </SubSectionHeader>
                    <CargoTankTemperaturesWrapper>
                        <GridWrapper>
                            <ListHeaderStyle>
                                <li>
                                    <label>Cargo</label>
                                    <label>Trade name</label>
                                    <label>Tank</label>
                                    <label>On board</label>
                                    <label>Temp.</label>
                                    <label>Load. temp.</label>
                                </li>
                            </ListHeaderStyle>
                        </GridWrapper>
                        <GridWrapper>
                            {getCargoTankTemperatureList()?.map((o) => (
                                <li key={o.cargoNumber + '-' + o.tankId}>
                                    <span>{o.cargoNumber}</span>
                                    <span className="col-trade-name">
                                        <a
                                            href={'https://commoditybook.odfjell.com/#/product/' + o.commodityId}
                                            target="_blank"
                                            title="Click here to open the Commodity Book"
                                            rel="noreferrer"
                                        >
                                            {o.tradeName}
                                            <FontAwesomeIcon className="external-link-icon" icon="external-link-alt" />
                                        </a>
                                    </span>
                                    <span className="centered-text">{o.tankName}</span>
                                    <span className="centered-checkbox">
                                        <OdfjellInputComponent
                                            type={'checkbox'}
                                            isReadOnly={props.isReadOnly}
                                            dataObject={o}
                                            name={'isOnBoard'}
                                            value={o.isOnBoard}
                                            onChange={onInputChange}
                                        />
                                    </span>
                                    {!o.isOnBoard && (
                                        <>
                                            <span className="temperature-input" />
                                            <span className="temperature-input" />
                                        </>
                                    )}
                                    {o.isOnBoard && (
                                        <>
                                            <span className="temperature-input">
                                                <OdfjellInputComponent
                                                    type={'number'}
                                                    isReadOnly={props.isReadOnly}
                                                    unit={'℃'}
                                                    value={o.temperature}
                                                    name={'temperature'}
                                                    min={0}
                                                    max={100}
                                                    dataObject={o}
                                                    onChange={onInputChange}
                                                    allowNegativeValues={true}
                                                />
                                            </span>
                                            <span className="temperature-input">
                                                <OdfjellInputComponent
                                                    type={'number'}
                                                    isReadOnly={props.isReadOnly}
                                                    unit={'℃'}
                                                    value={o.cargoLoadingTemperature}
                                                    name={'cargoLoadingTemperature'}
                                                    min={0}
                                                    max={100}
                                                    dataObject={o}
                                                    onChange={onInputChange}
                                                    allowNegativeValues={true}
                                                />
                                            </span>
                                        </>
                                    )}
                                </li>
                            ))}
                        </GridWrapper>
                    </CargoTankTemperaturesWrapper>
                    {hasEmptyValuesLocal && <ErrorText>You must enter all data before saving.</ErrorText>}
                </SectionInnerWrapper>
            )}
        </>
    );
};

export default CargoTankTemperaturesComponent;
